/**
 * useDocCalculation hook
 *
 * Calls ERPNext server to calculate document totals (taxes, discounts, grand total).
 * Uses debouncing to avoid excessive API calls during rapid field changes.
 *
 * This hook ensures ERPNext is the SINGLE SOURCE OF TRUTH for all financial calculations.
 * DO NOT perform financial calculations client-side.
 *
 * REJECTION HANDLING:
 * When the server rejects a change (validation error, end-of-life item, etc.),
 * the hook calls onRejected with the last known good state so the caller can rollback.
 * This ensures invalid changes never persist in the UI.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useAuthStore } from "@/lib/store/authStore";
import { tenantServer } from "@/lib/api/tenantServerClient";
import type { Warning } from "@/lib/warnings";

// Response type from the backend
export interface CalculationResult {
  // Totals
  total?: number;
  net_total?: number;
  base_net_total?: number;
  total_taxes_and_charges?: number;
  base_total_taxes_and_charges?: number;
  grand_total?: number;
  base_grand_total?: number;
  rounded_total?: number;
  base_rounded_total?: number;
  rounding_adjustment?: number;
  base_rounding_adjustment?: number;
  in_words?: string;
  base_in_words?: string;
  // Quantities
  total_qty?: number;
  total_net_weight?: number;
  // Discounts - ALL discount fields from server
  discount_amount?: number;
  base_discount_amount?: number;
  discount_percentage?: number;
  additional_discount_percentage?: number;
  additional_discount_amount?: number;
  apply_discount_on?: string;
  // Child tables
  items?: Array<{
    idx: number;
    item_code?: string;
    qty: number;
    rate: number;
    amount: number;
    net_rate: number;
    net_amount: number;
    base_rate: number;
    base_amount: number;
    base_net_rate: number;
    base_net_amount: number;
  }>;
  taxes?: Array<{
    idx: number;
    charge_type?: string;
    account_head?: string;
    description?: string;
    rate: number;
    tax_amount: number;
    base_tax_amount: number;
    total: number;
    base_total: number;
    included_in_print_rate?: 0 | 1;
  }>;
}

/** Warning data from backend API */
export interface ApiWarning {
  severity: 'warning' | 'error' | 'info';
  title: string;
  message: string;
  source?: string;
}

export interface UseDocCalculationOptions {
  /** Doctype (e.g., "Sales Order") */
  doctype: string;
  /** Debounce delay in ms (default: 500ms) */
  debounceMs?: number;
  /** Callback when calculation succeeds - receives calculated values */
  onSuccess?: (result: CalculationResult) => void;
  /** Callback when calculation fails (network error, etc.) */
  onError?: (error: string) => void;
  /** Callback when backend returns warnings (validation messages, etc.) */
  onWarning?: (warning: Omit<Warning, 'id' | 'timestamp'>) => void;
  /**
   * Callback when server REJECTS the change (validation error).
   * Receives the last known good state so caller can rollback.
   * CRITICAL: You MUST implement this to prevent invalid state from persisting.
   */
  onRejected?: (lastGoodState: Record<string, unknown>, error: string) => void;
  /** Whether calculation is enabled (e.g., disable for submitted docs) */
  enabled?: boolean;
}

export interface UseDocCalculationReturn {
  /**
   * Trigger a calculation with the given doc data.
   * @param docData - Current document state (will be validated by server)
   * @param previousState - State BEFORE this change (for rollback on rejection)
   */
  calculate: (docData: Record<string, unknown>, previousState?: Record<string, unknown>) => void;
  /** Latest calculation result */
  result: CalculationResult | null;
  /** Whether a calculation is in progress */
  isCalculating: boolean;
  /** Error message if calculation failed */
  error: string | null;
  /** Whether the last calculation was rejected (requires rollback) */
  wasRejected: boolean;
  /** Clear the current result and error */
  reset: () => void;
}

/**
 * Hook to calculate document totals using ERPNext server
 *
 * @example
 * const { calculate, wasRejected } = useDocCalculation({
 *   doctype: "Sales Order",
 *   onSuccess: (data) => {
 *     // Update form with calculated values
 *     setFormData(prev => ({ ...prev, grand_total: data.grand_total }));
 *   },
 *   onRejected: (lastGoodState, error) => {
 *     // Rollback to previous state - CRITICAL for data integrity
 *     setFormData(lastGoodState);
 *   },
 *   onWarning: showWarning, // Display warning modal to user
 * });
 *
 * // When field changes, pass both new and previous state
 * const handleFieldChange = (field, value) => {
 *   const previousState = { ...formData };
 *   const newState = { ...formData, [field]: value };
 *   setFormData(newState);
 *   calculate(newState, previousState);
 * };
 */
export function useDocCalculation(
  options: UseDocCalculationOptions
): UseDocCalculationReturn {
  const {
    doctype,
    debounceMs = 500,
    onSuccess,
    onError,
    onWarning,
    onRejected,
    enabled = true,
  } = options;

  const frappeClient = useAuthStore((state) => state.frappeClient);
  const authMode = useAuthStore((state) => state.authMode);
  const cpToken = useAuthStore((state) => state.cpToken);

  const [result, setResult] = useState<CalculationResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wasRejected, setWasRejected] = useState(false);

  // Refs for managing debounce and abort
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingDocDataRef = useRef<Record<string, unknown> | null>(null);
  const pendingPreviousStateRef = useRef<Record<string, unknown> | null>(null);

  // Track last successfully validated state for rollback
  const lastGoodStateRef = useRef<Record<string, unknown> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Internal function to actually call the API
  const executeCalculation = useCallback(
    async (docData: Record<string, unknown>, previousState: Record<string, unknown> | null) => {
      // Check authentication based on auth mode
      const isAuthenticated = authMode === 'control_plane' ? !!cpToken : !!frappeClient;

      if (!isAuthenticated) {
        console.warn("[useDocCalculation] Not authenticated - skipping calculation", { authMode });
        setError("Not authenticated");
        onError?.("Not authenticated");
        return;
      }

      if (!enabled) {
        console.log("[useDocCalculation] Calculation disabled - skipping");
        return;
      }

      // Abort any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      setIsCalculating(true);
      setError(null);
      setWasRejected(false);

      // Log what we're sending
      console.log("[useDocCalculation] Triggering calculation:", {
        doctype,
        authMode,
        itemCount: (docData.items as unknown[])?.length || 0,
        hasCompany: !!docData.company,
        company: docData.company,
        additionalDiscount: docData.additional_discount_percentage,
        hasPreviousState: !!previousState,
      });

      try {
        // Call API based on auth mode
        let response: {
          success: boolean;
          data?: CalculationResult;
          error?: string;
          error_type?: string;
          warnings?: ApiWarning[];
        };

        if (authMode === 'control_plane') {
          // Use tenantServer for control_plane mode (JWT auth)
          response = await tenantServer.call<{
            success: boolean;
            data?: CalculationResult;
            error?: string;
            error_type?: string;
            warnings?: ApiWarning[];
          }>("zaviago_backend.api.doc_calculation.calculate_document_totals", {
            doctype,
            doc_data: JSON.stringify(docData),
          });
        } else {
          // Use frappeClient for direct mode (session auth)
          response = await frappeClient!.call<{
            success: boolean;
            data?: CalculationResult;
            error?: string;
            error_type?: string;
            warnings?: ApiWarning[];
          }>("zaviago_backend.api.doc_calculation.calculate_document_totals", {
            doctype,
            doc_data: JSON.stringify(docData),
          });
        }

        console.log("[useDocCalculation] Server response:", response);

        // Check if aborted
        if (abortControllerRef.current?.signal.aborted) {
          console.log("[useDocCalculation] Request was aborted");
          return;
        }

        if (response.success && response.data) {
          // SUCCESS: Server accepted the change
          console.log("[useDocCalculation] Calculation success:", {
            net_total: response.data.net_total,
            grand_total: response.data.grand_total,
            rounded_total: response.data.rounded_total,
            total_qty: response.data.total_qty,
          });

          // Update last known good state
          lastGoodStateRef.current = docData;

          setResult(response.data);
          setError(null);
          setWasRejected(false);
          onSuccess?.(response.data);

          // Process any informational warnings (non-blocking)
          if (response.warnings && response.warnings.length > 0 && onWarning) {
            for (const warning of response.warnings) {
              if (warning.severity === 'info') {
                onWarning({
                  severity: warning.severity,
                  title: warning.title,
                  message: warning.message,
                  source: warning.source || 'ERPNext',
                  dismissable: true,
                });
              }
            }
          }
        } else {
          // REJECTION: Server rejected the change - MUST ROLLBACK
          const errorMsg = response.error || "Validation failed";
          console.error("[useDocCalculation] REJECTED:", errorMsg, response.error_type);

          setError(errorMsg);
          setWasRejected(true);

          // Show warning modal to user
          if (response.warnings && response.warnings.length > 0 && onWarning) {
            console.log("[useDocCalculation] Processing rejection warnings:", response.warnings);
            for (const warning of response.warnings) {
              onWarning({
                severity: warning.severity,
                title: warning.title,
                message: warning.message,
                source: warning.source || 'ERPNext',
                dismissable: true,
              });
            }
          } else if (onWarning) {
            // No specific warnings, show generic error
            onWarning({
              severity: 'error',
              title: 'Action Not Allowed',
              message: errorMsg,
              source: 'ERPNext',
              dismissable: true,
            });
          }

          // CRITICAL: Trigger rollback to last good state
          // Priority: previousState (from this change) > lastGoodStateRef (from last success)
          const rollbackState = previousState || lastGoodStateRef.current;
          if (rollbackState && onRejected) {
            console.log("[useDocCalculation] Triggering rollback to previous state");
            onRejected(rollbackState, errorMsg);
          } else if (!onRejected) {
            console.warn("[useDocCalculation] WARNING: onRejected not provided - invalid state may persist!");
          }

          onError?.(errorMsg);
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === "AbortError") {
          console.log("[useDocCalculation] Request aborted");
          return;
        }

        const errorMsg =
          err instanceof Error ? err.message : "Calculation failed";
        console.error("[useDocCalculation] Exception:", errorMsg);
        setError(errorMsg);

        // Network errors should also trigger rollback
        const rollbackState = previousState || lastGoodStateRef.current;
        if (rollbackState && onRejected) {
          console.log("[useDocCalculation] Triggering rollback due to network error");
          onRejected(rollbackState, errorMsg);
          setWasRejected(true);
        }

        onError?.(errorMsg);
      } finally {
        setIsCalculating(false);
      }
    },
    [frappeClient, authMode, cpToken, doctype, enabled, onSuccess, onError, onWarning, onRejected]
  );

  // Public calculate function with debouncing
  const calculate = useCallback(
    (docData: Record<string, unknown>, previousState?: Record<string, unknown>) => {
      // Store the latest doc data and previous state
      pendingDocDataRef.current = docData;
      pendingPreviousStateRef.current = previousState || null;

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        if (pendingDocDataRef.current) {
          executeCalculation(pendingDocDataRef.current, pendingPreviousStateRef.current);
          pendingDocDataRef.current = null;
          pendingPreviousStateRef.current = null;
        }
      }, debounceMs);
    },
    [executeCalculation, debounceMs]
  );

  // Reset function
  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setWasRejected(false);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return {
    calculate,
    result,
    isCalculating,
    error,
    wasRejected,
    reset,
  };
}
